import { useState } from 'react';
import { useAuthContext } from '../../contexts/AuthContext';
import { changePassword } from '../../service/AuthService';

const btn = {
    primary:   'gradient-bg text-white px-6 py-2.5 rounded-lg font-medium hover:opacity-90 transition text-sm',
    secondary: 'border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition text-sm',
};
const input    = 'w-full px-4 py-2 border border-gray-300 rounded-lg text-sm transition focus:outline-none focus:ring-2 focus:ring-blue-400';
const inputRO  = 'w-full px-4 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed';
const overlay  = 'fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50';
const modal    = 'bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm text-center';

const PASSWORD_RULES = [
    { label: 'At least 8 characters',    test: (p) => p.length >= 8 },
    { label: 'One uppercase letter',      test: (p) => /[A-Z]/.test(p) },
    { label: 'One number',               test: (p) => /[0-9]/.test(p) },
    { label: 'One special character',    test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?]/.test(p) },
];

const AccountSection = () => {
    const { user } = useAuthContext();

    const [currentPassword,  setCurrentPassword]  = useState('');
    const [newPassword,      setNewPassword]      = useState('');
    const [confirmPassword,  setConfirmPassword]  = useState('');
    const [loading,          setLoading]          = useState(false);
    const [successModal,     setSuccessModal]     = useState(false);
    const [errorModal,       setErrorModal]       = useState({ show: false, message: '' });

    const allRulesPassed  = PASSWORD_RULES.every(r => r.test(newPassword));
    const passwordsMatch  = newPassword === confirmPassword && confirmPassword.length > 0;
    const canSubmit       = currentPassword && allRulesPassed && passwordsMatch && !loading;

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setLoading(true);
        try {
            await changePassword(currentPassword, newPassword);
            resetForm();
            setSuccessModal(true);
        } catch (err) {
            setErrorModal({ show: true, message: err.message || 'Failed to change password. Please try again.' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="accountSection" className="dashboard-section">
            <div className="bg-white rounded-2xl shadow-sm p-6">
                <h2 className="text-xl font-semibold mb-6 text-gray-800">My Account</h2>

                {/* Profile photo — not yet implemented
                <div className="mb-6">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Profile Picture</label>
                    <div className="flex items-center space-x-4">
                        <div className="h-16 w-16 rounded-full gradient-bg flex items-center justify-center flex-shrink-0">
                            <i className="fas fa-user text-white text-2xl"></i>
                        </div>
                        <div>
                            <button type="button" className={btn.secondary}>Change Photo</button>
                            <button type="button" className="text-red-500 hover:text-red-700 ml-3 text-sm">Remove</button>
                        </div>
                    </div>
                </div>
                */}

                {/* Email — read only */}
                <div className="mb-8">
                    <label className="block text-gray-700 font-medium mb-2 text-sm">Email</label>
                    <input type="email" className={inputRO} value={user?.email ?? ''} readOnly />
                    <p className="text-gray-400 text-xs mt-1">Email cannot be changed here. Contact your administrator.</p>
                </div>

                <hr className="border-gray-100 mb-8" />

                {/* Change password */}
                <h3 className="text-base font-semibold text-gray-700 mb-5">Change Password</h3>
                <form onSubmit={handleSubmit} className="space-y-5 max-w-md">
                    <div>
                        <label className="block text-gray-700 font-medium mb-1 text-sm">Current Password</label>
                        <input
                            type="password"
                            className={input}
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-1 text-sm">New Password</label>
                        <input
                            type="password"
                            className={input}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            autoComplete="new-password"
                            disabled={loading}
                        />
                        {newPassword.length > 0 && (
                            <ul className="mt-2 space-y-1">
                                {PASSWORD_RULES.map((rule) => (
                                    <li key={rule.label} className={`flex items-center gap-2 text-xs ${rule.test(newPassword) ? 'text-green-600' : 'text-gray-400'}`}>
                                        <i className={`fas ${rule.test(newPassword) ? 'fa-check-circle' : 'fa-circle'} text-xs`}></i>
                                        {rule.label}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div>
                        <label className="block text-gray-700 font-medium mb-1 text-sm">Confirm New Password</label>
                        <input
                            type="password"
                            className={`${input} ${confirmPassword.length > 0 ? (passwordsMatch ? 'border-green-400 focus:ring-green-400' : 'border-red-400 focus:ring-red-400') : ''}`}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            autoComplete="new-password"
                            disabled={loading}
                        />
                        {confirmPassword.length > 0 && !passwordsMatch && (
                            <p className="text-red-500 text-xs mt-1">Passwords do not match.</p>
                        )}
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            className={`${btn.primary} ${!canSubmit ? 'opacity-50 cursor-not-allowed' : ''}`}
                            disabled={!canSubmit}
                        >
                            {loading ? 'Saving…' : 'Save Changes'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Success Modal */}
            {successModal && (
                <div className={overlay}>
                    <div className={modal}>
                        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-check text-green-500 text-xl"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Password updated</h3>
                        <p className="text-gray-500 text-sm mb-5">Your password has been changed successfully.</p>
                        <button className={btn.primary} onClick={() => setSuccessModal(false)}>Close</button>
                    </div>
                </div>
            )}

            {/* Error Modal */}
            {errorModal.show && (
                <div className={overlay}>
                    <div className={modal}>
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-exclamation text-red-500 text-xl"></i>
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">Error</h3>
                        <p className="text-gray-500 text-sm mb-5">{errorModal.message}</p>
                        <button className={btn.primary} onClick={() => setErrorModal({ show: false, message: '' })}>Close</button>
                    </div>
                </div>
            )}
        </section>
    );
};

export default AccountSection;
